import './App.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  primaryPaletteColor,
  secondaryPaletteColor,
} from './configurations/global.config';
import { enableMapSet } from 'immer';

import AppLayout from './components/app-layout/app-layout';
import { AuthProvider } from './contexts/user-context/user-context';
import { Routes, Route } from 'react-router-dom';
import DeliveryTracker from './components/delivery-tracker/delivery-tracker';
import LearnPage from './components/public/learn/learn-page';
import PublicPageWrapper from './components/common/public-page-wrapper/public-page-wrapper';
import { PaginationProvider } from './contexts/pagination-context';
import { OrgSettingsContext, OrgSettingsProvider } from './contexts/org-settings-context';
import { useContext, useEffect, useMemo } from 'react';
import { SnackBarProvider } from './contexts/snackbar';
import { VideoTutorialProvider } from './contexts/video-tutorial-context/video-tutorial-context';
import { QuickFiltersProvider } from './contexts/quick-filters-context';
import { MapStyleProvider } from './contexts/map-style-context';
import { ToastContainer } from 'react-toastify';
import { DomainForcedOrganisationProvider } from './contexts/domain-forced-organisation-context';
import PushNotificationsInitializer from './PushNofiticationsInitializer';
import { NotificationProvider } from './contexts/user-context/notification-context';
import { Health } from './components/common/health';
import { SelectedBranchProvider } from './contexts/selected-branch-context';
import { AxiosInterceptorProvider } from './contexts/axios-interceptor-provider';
import { HolidayThemeProvider } from './contexts/holiday-theme-context';
import { HolidayTheme } from './components/common/holiday-theme/holiday-theme';
import { BranchFilterProvider } from './contexts/branch-filter-context';
import { PrinterProvider } from './contexts/printer-context';


interface ThemeWrapperProps {
  children: React.ReactNode;
}

const DefaultThemeWrapper = ({ children }: ThemeWrapperProps) => {
  const theme = createTheme({
    palette: {
      primary: {
        main: primaryPaletteColor,
      },
      secondary: {
        main: secondaryPaletteColor,
      },
    },
  });

  theme.typography.h3 = {
    fontSize: '1.6rem',
    '@media (min-width:600px)': {
      fontSize: '2rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2.4rem',
    },
  };

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

const CustomThemeProvider = ({ children }: ThemeWrapperProps) => {
  const { color } = useContext(OrgSettingsContext);
  
  useEffect(()=>{
    console.log("here is color: ", color)
  }, [color])
  
  const theme = useMemo(()=>createTheme({
    palette: {
      primary: {
        main: color ? color.length === 0 ? primaryPaletteColor : color : primaryPaletteColor,
      },
      secondary: {
        main: secondaryPaletteColor,
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            // Puts the label into notched position
            '.MuiInputLabel-outlined:has(+ .MuiOutlinedInput-root > .MuiOutlinedInput-input:-webkit-autofill)': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
            // Creates a hole around the text inside the border
            '.MuiOutlinedInput-input:-webkit-autofill + .MuiOutlinedInput-notchedOutline > legend': {
              maxWidth: '100%',
            },
          },
        },
      },
    }  
  }), [color]);

  theme.typography.h3 = {
    fontSize: '1.6rem',
    '@media (min-width:600px)': {
      fontSize: '2rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2.4rem',
    },
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--mui-primary-color', theme.palette.primary.main);
  }, [theme]);


  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};


function App() {
  enableMapSet();
  return (
    <HolidayThemeProvider>
      <div className="app">  
        <SnackBarProvider>
          <VideoTutorialProvider>
          <Routes>
          <Route path="/health" element={<Health />}>
          </Route>
            <Route path="/tracking">
              <Route
                path=":hash"
                element={
                  <DefaultThemeWrapper>
                    <PublicPageWrapper>
                      <DeliveryTracker />
                    </PublicPageWrapper>
                  </DefaultThemeWrapper>
                }
              />
            </Route>

            <Route path="/learn/tsc-802" element={
              <DefaultThemeWrapper>
                <PublicPageWrapper>
                  <LearnPage />
                </PublicPageWrapper>
              </DefaultThemeWrapper>
            } />

          <Route
            path="*"
            element={
              <PaginationProvider>
              <AuthProvider>
               <SelectedBranchProvider>
               <BranchFilterProvider>
               <AxiosInterceptorProvider>
               <NotificationProvider>
               <PushNotificationsInitializer /> 
                <OrgSettingsProvider>
                  <CustomThemeProvider>
                    <DomainForcedOrganisationProvider>
                      <QuickFiltersProvider>
                        <MapStyleProvider>
                        <PrinterProvider>
                        <HolidayTheme />
                        <AppLayout />
                        </PrinterProvider>
                        </MapStyleProvider>
                      </QuickFiltersProvider>
                      </DomainForcedOrganisationProvider>
                  </CustomThemeProvider>
                </OrgSettingsProvider>
                </NotificationProvider>
                </AxiosInterceptorProvider>
                </BranchFilterProvider>
                </SelectedBranchProvider>
              </AuthProvider>
            </PaginationProvider>
              }
                 
            ></Route>
          </Routes>
        </VideoTutorialProvider>
        </SnackBarProvider>


      

        <ToastContainer />
      </div>
    </HolidayThemeProvider>
  );
}

export default App;
